import { useEffect, FunctionComponent, useState, useCallback } from 'react';
import { logError, logInfo } from '@betty/toolkit/logging';
import { selectAccount } from '@betty/account/state';
import { useSelector } from '@betty/toolkit/state';
import { Loader } from '@betty/moonlight';
import { loadMkodoScript } from '../utils/mkodo';
import { MGeoJSGatewayStatus, MGeoJSPermissionStatus } from '../state';
import LocationError from '../components/LocationError/LocationError';
import LocationDenied from '../components/LocationDenied/LocationDenied';
import GamblingForbidden from '../components/GamblingForbidden/GamblingForbidden';

const COMMON_STYLES = { flex: 1, paddingHorizontal: 'space-600' } as const;

const withMkodoCheck = <T extends object>(Component: FunctionComponent<T>) => {
  function MkodoCheck(props: T) {
    const [gatewayStatus, setGatewayStatus] = useState<MGeoJSGatewayStatus>();
    const [permissionStatus, setPermissionStatus] =
      useState<MGeoJSPermissionStatus>();

    const retrievePermissionStatus = useCallback(
      () =>
        new Promise<MGeoJSPermissionStatus>((resolve) => {
          navigator.geolocation.getCurrentPosition(
            () => {
              resolve('GRANTED');
            },
            (error) => {
              resolve(
                error.code === error.PERMISSION_DENIED
                  ? 'DENIED'
                  : 'UNAVAILABLE',
              );
            },
            {
              enableHighAccuracy: false,
              maximumAge: 60 * 60 * 1000, // 1 hour
            },
          );
        }),
      [],
    );

    const initMkodo = useCallback(async () => {
      try {
        await loadMkodoScript();

        const permissionStatus = await retrievePermissionStatus();

        setPermissionStatus(permissionStatus);

        if (!window.mGeoJS || permissionStatus !== 'GRANTED') {
          return;
        }

        const listenerTokens = [
          window.mGeoJS.subscribe('onPingResponse', ({ gatewayStatus }) => {
            setGatewayStatus(gatewayStatus);
          }),

          window.mGeoJS.subscribe('outOfRegion', () => {
            logInfo('Mkodo', 'outOfRegion');

            setGatewayStatus('BLACKLISTED');
          }),

          window.mGeoJS.subscribe('noGeoLocation', (status) => {
            logError('Mkodo error', {
              type: 'noGeoLocation',
              data: { status },
            });

            setPermissionStatus(status === 'DENIED' ? 'DENIED' : 'UNAVAILABLE');
          }),

          window.mGeoJS.subscribe('maxRetriesExceeded', () => {
            logError('Mkodo error', { type: 'maxRetriesExceeded' });

            setPermissionStatus('UNAVAILABLE');
          }),
        ];

        window.mGeoJS.start({ ping: true });

        return listenerTokens;
      } catch (error) {
        logError('Mkodo error', error);
      }
    }, [retrievePermissionStatus]);

    useEffect(() => {
      const listenerTokensPromise = initMkodo();

      return () => {
        window.mGeoJS?.stop();

        listenerTokensPromise.then((tokens) => {
          if (tokens) {
            tokens.forEach((token) => {
              window.mGeoJS?.unsubscribe(token);
            });
          }
        });
      };
    }, [initMkodo]);

    if (permissionStatus === 'DENIED') {
      return <LocationDenied {...COMMON_STYLES} />;
    }

    if (!gatewayStatus || !permissionStatus) {
      return <Loader {...COMMON_STYLES} />;
    }

    if (gatewayStatus === 'BLOCKED' || permissionStatus === 'UNAVAILABLE') {
      return <LocationError {...COMMON_STYLES} />;
    }

    if (gatewayStatus === 'BLACKLISTED') {
      return <GamblingForbidden {...COMMON_STYLES} />;
    }

    return <Component {...props} />;
  }

  return function WhitelistedCheck(props: T) {
    const { isMkodoWhitelisted } = useSelector(selectAccount) || {};

    return isMkodoWhitelisted ? (
      <Component {...props} />
    ) : (
      <MkodoCheck {...props} />
    );
  };
};

export default withMkodoCheck;
